import type { CommentFieldValue } from "@/shared/components/comment-field/use-comment-field";
import type { FieldValidationError } from "@/shared/types/workflow/workflow-form.types";
import type { CommentField, RichText } from "@spz/shared-domain";
import { RichTextUtils } from "@spz/shared-domain";
import type { TFunction } from "i18next";

/**
 * Validates the content of a comment field
 */
function validateCommentContent({
  field,
  value,
  t,
}: {
  field: CommentField;
  value: CommentFieldValue;
  t: TFunction;
}): FieldValidationError | undefined {
  if (!field.validation?.content) {
    return undefined;
  }

  const contentText = RichTextUtils.getTextContent(value.content as RichText);

  if (
    field.validation.content.min_length &&
    contentText.length < field.validation.content.min_length
  ) {
    const min = field.validation.content.min_length;
    return {
      translationKey: "workflow_validation_min_length",
      type: "minLength",
      params: { count: min },
      message: t("workflow_validation_min_length", { count: min }),
    };
  }

  if (
    field.validation.content.max_length &&
    contentText.length > field.validation.content.max_length
  ) {
    const max = field.validation.content.max_length;
    return {
      translationKey: "workflow_validation_max_length",
      type: "maxLength",
      params: { count: max },
      message: t("workflow_validation_max_length", { count: max }),
    };
  }

  return undefined;
}

/**
 * Validates the attachment of a comment field
 */
function validateCommentAttachment({
  field,
  value,
  t,
}: {
  field: CommentField;
  value: CommentFieldValue;
  t: TFunction;
}): FieldValidationError | undefined {
  if (!field.validation?.attachment) {
    return undefined;
  }

  // Check if attachment is required
  if (field.validation.attachment.required && !value.attachment) {
    return {
      translationKey: "workflow_validation_attachment_required",
      type: "required",
      message: t("workflow_validation_attachment_required"),
    };
  }

  // If no attachment, no further validation needed
  if (!value.attachment) {
    return undefined;
  }

  const file = value.attachment;
  const fileSizeMb = file.size / (1024 * 1024);

  if (
    field.validation.attachment.max_size_mb &&
    fileSizeMb > field.validation.attachment.max_size_mb
  ) {
    const max = field.validation.attachment.max_size_mb;
    return {
      translationKey: "workflow_validation_file_size",
      type: "max",
      params: { max },
      message: t("workflow_validation_file_size", { max }),
    };
  }

  if (
    field.validation.attachment.accepted_formats &&
    !field.validation.attachment.accepted_formats.includes(file.type)
  ) {
    return {
      translationKey: "workflow_validation_file_format",
      type: "format",
      message: t("workflow_validation_file_format"),
    };
  }

  return undefined;
}

/**
 * Validates a comment field according to its validation constraints
 */
export function validateComment({
  field,
  value,
  t,
}: {
  field: CommentField;
  value: CommentFieldValue;
  t: TFunction;
}): FieldValidationError | undefined {
  if (!field.validation) {
    return undefined;
  }

  const contentError = validateCommentContent({ field, value, t });
  if (contentError) {
    return contentError;
  }

  return validateCommentAttachment({ field, value, t });
}
